import { taxIdWord } from "../../../model/steuerbegriffe";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { listeVorlagen } from "../../../model/beleg-vorlagen";
import { BELEG_ARTEN } from "../einstellungen/PdfVorlagenEditor";
import { BeispielEtikett } from "../../../components/Beispiel";
import { useStaffWorkspace, type StaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading, NoAccess } from "../../../components/States";
import { PillView, useAction } from "../../../components/ui";
import { date, initials, money, monthLabel, numberLocale, moneyWhole } from "../../../lib/format";
import { BrandDialog, ContactsEditor, orgCountryOf } from "./BrandDialog";
import { saveBrandNote } from "./data";
import { useBase, useBrandContacts, useBrandNote } from "./hooks";
import { brandGaps, brandProfile, brandTermsFallback, contactFor, countryLabel, nameOf, pill, sumsLabel, zoneHint, type Base, type BrandProfile, type ContactRow } from "./model";
import { BookingTable, ContractList, DealTable, DemoNote, Fact, HIDDEN_AMOUNTS, HIDDEN_FINANCIALS, InvoiceTable, Kpi, RankRows, useSicht } from "./parts";
import { t, ta } from "../../../lib/sprache";
import { MarkenMailZustellung } from "./CreatorZusatz";
import { UstIdPruefung } from "../../../components/UstIdPruefung";

/** Marken-Profil 360° (11_app_agentur/marken-detail.html, Umfang AD-030 des Altportals). */
export function MarkenDetail() {
  const { brandId = "" } = useParams();
  const ws = useStaffWorkspace();
  const { base, error, loading, noAccess, retry } = useBase(ws);
  const contacts = useBrandContacts(ws.org.org_id);
  const profile = useMemo(() => (base ? brandProfile(base, brandId) : null), [base, brandId]);

  const err = error ?? contacts.error;
  if (err) return <ErrorState error={err} retry={() => { retry(); void contacts.refetch(); }} />;
  if (noAccess) return <NoAccess />;
  if (loading || !base || contacts.isPending) return <Loading />;
  if (!profile) return <NoAccess text={t("Diese Marke gibt es nicht oder sie ist für dein Konto nicht freigegeben.", "This brand does not exist or is not available to your account.")} />;
  return <Detail ws={ws} base={base} p={profile} contacts={contacts.data ?? []} />;
}

const LANG: Record<string, string> = { get de() { return t("Deutsch", "German"); }, get en() { return t("Englisch", "English"); } };

function Detail({ ws, base, p, contacts }: { ws: StaffWorkspace; base: Base; p: BrandProfile; contacts: ContactRow[] }) {
  const b = p.brand;
  const [edit, setEdit] = useState(false);
  const own = contacts.filter((c) => c.brand_id === b.id);
  // Eine Regel für die Empfänger (contactFor): Rechnung an die Rechnungsmail der Marke, sonst an den Kontakt „Rechnung“
  const invoiceTo = contactFor("invoice", b, contacts);
  const contractTo = contactFor("contract", b, contacts);
  const main = own.find((c) => c.name === contractTo?.name) ?? own.find((c) => c.is_invoice_recipient) ?? own[0] ?? null;
  const mail = invoiceTo?.email ?? main?.email ?? null;
  const gaps = b.kind === "brand" ? brandGaps(b, orgCountryOf(base), contacts) : [];
  const m = (c: number) => money(c, base.currency);
  // Recht „Beträge sehen“ und „Kennzahlen und Agenturumsatz sehen“ (Befund 9)
  const { amounts, financials: finanzSicht } = useSicht();
  // Studio (Creator-Konto): kein Agenturanteil, kein Abrechnungsweg, keine Creator-Rangliste (es gibt nur das eigene Konto)
  const ich = !!ws.isCreator;
  const financials = finanzSicht && !ich;
  const year = base.today.slice(0, 4);
  const talentName = (id: string | null) => (id ? nameOf(base.talentById.get(id)) : "–");
  const latestTalent = p.deals[0]?.deal.talent_id ?? null;
  const terms = b.payment_terms_days ?? brandTermsFallback(base.settings);
  const overdueCount = p.overdue?.count ?? 0;
  const address = [b.street, [b.postal_code, b.city].filter(Boolean).join(" "), b.country ? countryLabel(b.country) : null].filter(Boolean).join(", ");

  return (
    <main id="main" className="content tk-detail tk-sd">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account")}</div>
          <h1>{b.name}<BeispielEtikett von={b} /></h1>
          <p>{[b.legal_name, b.country ? countryLabel(b.country) : null, b.vat_id, b.brand_number ? t(`Marke Nr. ${b.brand_number}`, `Brand no. ${b.brand_number}`) : null].filter(Boolean).join(" · ")}</p>
        </div>
        <div className="row">
          <Link className="btn" to="/agentur/marken">{t("Alle Marken", "All brands")}</Link>
          <button className="" onClick={() => setEdit(true)}>{t("Bearbeiten", "Edit")}</button>
        </div>
      </div>

      {gaps.length > 0 && (
        <div className="notice" role="status">
          {t("Für Rechnungen an diese Marke fehlt noch: ", "Still missing for invoices to this brand: ")}{gaps.join(", ")}. <button type="button" onClick={() => setEdit(true)}>{t("Jetzt ergänzen", "Complete now")}</button>
        </div>
      )}
      <MarkenMailZustellung brandId={b.id} timeZone={ws.org.timezone} onEdit={() => setEdit(true)} />
      {p.overdue && (
        <div className="integrity-banner" role="alert">
          <p>
            <strong>{p.overdue.count === 1 ? t("1 Rechnung überfällig", "1 invoice overdue") : t(`${p.overdue.count} Rechnungen überfällig`, `${p.overdue.count} invoices overdue`)}</strong>{amounts ? t(` · Summe ${sumsLabel(p.overdue.sums, money)}`, ` · total ${sumsLabel(p.overdue.sums, money)}`) : ""}{t(", älteste seit ", ", oldest since ")}{p.overdue.oldestDays} {p.overdue.oldestDays === 1 ? t("Tag", "day") : t("Tagen", "days")}{p.overdue.oldestNumber ? ` (${p.overdue.oldestNumber})` : ""}
          </p>
          <a className="btn" href="#rechnungen">Details</a>
        </div>
      )}

      <div className="grid tk-oben">
        <section className="panel stack">
          <h3>{t("Stammdaten", "Master data")}</h3>
          <div className="row">
            <span className="avatar">{initials(b.name)}</span>
            <div>
              <strong>{b.name}</strong>
              <p>{mail ?? t("Keine E-Mail hinterlegt", "No email on file")}</p>
            </div>
          </div>
          <div className="row"><PillView pill={p.statusPill} /></div>
          <p>
            {t("Kontakt: ", "Contact: ")}{main ? main.name : "–"}{contractTo?.name && contractTo.name !== main?.name ? t(` · Vertrag: ${contractTo.name}`, ` · contract: ${contractTo.name}`) : ""}
            <br />
            {t("Rechnungen an: ", "Invoices to: ")}{invoiceTo?.email ?? "–"}
            <br />
            {t("Zahlungsziel: ", "Payment term: ")}{terms != null ? t(`${terms} Tage`, `${terms} days`) : "–"} · {b.country ?? "–"} · {b.default_currency ?? base.currency}
          </p>
          <div>
            <Fact label={t("Rechtlicher Name", "Legal name")}>{b.legal_name ?? "–"}</Fact>
            <Fact label={t("Anschrift", "Address")}>{address || "–"}</Fact>
            <Fact label={taxIdWord(b.country)}>{b.vat_id ?? "–"}</Fact>
            {/* Steuerprüfung L6: Stand der Prüfung bei VIES (geprüft am, gültig bzw. ungültig) */}
            {b.vat_id && <Fact label={t("VIES\u2011Prüfung", "VIES check")}><UstIdPruefung orgId={ws.org.org_id} vatId={b.vat_id} brandId={b.id} id="marke-vies" /></Fact>}
            <Fact label={t("Handelsregister", "Commercial register")}>{b.register_info ?? "–"}</Fact>
            <Fact label={t("Sprache", "Language")}>{b.language ? LANG[b.language] ?? b.language : ta("Nicht festgelegt (Mail wie die Agentur, Beleg nach Land)", "Not set (email like the agency, documents by country)", "Nicht festgelegt (Mail wie dein Konto, Beleg nach Land)", "Not set (email like your account, documents by country)")}</Fact>
            <Fact label={t("Steuerzone", "Tax zone")}>{zoneHint(b.country, orgCountryOf(base)).replace("Steuerzone: ", "")}</Fact>
            <Fact label={t("E‑Rechnung", "E-invoice")}>{b.e_invoice ? t("XRechnung geht mit jeder Rechnung mit", "XRechnung goes with every invoice") : (b.country === "DE" && (b as unknown as { is_business?: boolean | null }).is_business !== false && orgCountryOf(base) === "DE"
              // Steuerprüfung L11: Deutschland an Deutschland ab Stichtag automatisch (Einstellungen · Gutschriften, ohne Eintrag 1.1.2027)
              ? t("Nach Vorgabe: ab dem Stichtag der Pflicht automatisch (ohne Eintrag ab 1. Januar 2027), bis dahin nur PDF", "By default: automatically from the date of the obligation (without an entry from 1 January 2027), until then PDF only")
              : t("Nein, nur PDF", "No, PDF only"))}{b.buyer_reference ? t(` · Käuferreferenz ${b.buyer_reference}`, ` · buyer reference ${b.buyer_reference}`) : ""}</Fact>
            {b.kind === "brand" && !ich && (
              <Fact label={t("Abrechnungsweg", "Invoicing route")}>{b.invoice_route === "direct_to_brand" ? t("Creator direkt an die Marke", "Creator directly to the brand") : b.invoice_route === "via_agency" ? t("Über die Agentur", "Via the agency") : t("Agentur-Standard", "Agency default")}</Fact>
            )}
            {p.agency && (
              <Fact label={t("Betreut durch", "Managed by")}><Link to={`/agentur/marken/${p.agency.id}`}>{p.agency.name}</Link>{b.invoice_via_agency ? t(" · Rechnung an die Agentur", " · invoice to the agency") : t(" · Rechnung an die Marke", " · invoice to the brand")}</Fact>
            )}
          </div>
        </section>
        <section className="panel stack">
          <h3>{t("Zusammenarbeit", "Working together")}</h3>
          {amounts ? (
            <>
              <div className="metric num">{m(p.year.gross)}</div>
              <p>{t("Honorarvolumen netto · laufendes Jahr", "Fee volume net · current year")}</p>
            </>
          ) : (
            <>
              <div className="metric num">{p.activeDeals}</div>
              <p>{t("Aktive Kooperationen · ", "Active deals · ")}{p.deals.length} {t("insgesamt", "in total")}</p>
            </>
          )}
          {/* D-1: Aktionen nebeneinander, Hinweise zu gesperrten Knöpfen darunter */}
          <div className="row">
            {mail
              ? <a className="btn " href={`mailto:${mail}`}>{t("Nachricht", "Message")}</a>
              : <button className="" disabled aria-describedby="marke-ohne-mail">{t("Nachricht", "Message")}</button>}
            {!ich && (latestTalent
              ? <Link className="btn " to={`/agentur/creator/${latestTalent}/insights?marke=${b.id}`}>{t("Insights freigeben", "Share insights")}</Link>
              : <button className="" disabled aria-describedby="marke-ohne-creator">{t("Insights freigeben", "Share insights")}</button>)}
          </div>
          {!mail && <small id="marke-ohne-mail">{t("Keine E-Mail-Adresse hinterlegt.", "No email address on file.")}</small>}
          {!latestTalent && !ich && <small id="marke-ohne-creator">{t("Erst mit einer Kooperation lässt sich ein Creator für die Freigabe wählen.", "Only with a deal can a creator be chosen for sharing.")}</small>}
        </section>
      </div>

      {amounts && (
        <section>
          <div className="group-label">{t("Laufzeit", "term")}</div>
          <div className="overview-kpis talent-kpis total">
            {financials && <Kpi label={t("Agentur-Einnahmen", "Agency income")} chip={t("Laufzeit", "term")} value={m(p.lifetime.agency)} sub={t(`Provision und Gebühren netto · davon verbucht ${m(p.lifetime.bookedAgency)}`, `Commission and fees net · of which booked ${m(p.lifetime.bookedAgency)}`)} />}
            <Kpi label={t("Kooperationsvolumen", "Deal volume")} chip={t("Laufzeit", "term")} value={m(p.lifetime.gross)} sub={t("Budget, Zusatzpositionen und Gebühren · netto", "Budget, extra items and fees · net")} />
            <Kpi label={t("Ø Kooperationswert", "Ø deal value")} chip={t("Laufzeit", "term")} value={m(p.avgDeal)} sub={t(`${p.lifetime.deals === 1 ? t("1 Kooperation", "1 deal") : t(`${p.lifetime.deals} Kooperationen`, `${p.lifetime.deals} deals`)} mit Umsatz · netto`, `${p.lifetime.deals === 1 ? t("1 Kooperation", "1 deal") : t(`${p.lifetime.deals} Kooperationen`, `${p.lifetime.deals} deals`)} with revenue · net`)} />
          </div>
        </section>
      )}
      <section>
        <div className="group-label">{t("Zusammenarbeit", "Working together")}</div>
        <div className="overview-kpis period">
          <Kpi label={t("Kooperationen", "Deals")} value={p.deals.length} sub={ich ? t(`${p.activeDeals} aktiv`, `${p.activeDeals} active`) : t(`${p.talentCount === 1 ? t("1 Creator", "1 creator") : t(`${p.talentCount} Creator`, `${p.talentCount} creators`)} · ${p.activeDeals} aktiv`, `${p.talentCount === 1 ? t("1 Creator", "1 creator") : t(`${p.talentCount} Creator`, `${p.talentCount} creators`)} · ${p.activeDeals} active`)} />
          <Kpi label={t("Letzte Kooperation", "Last deal")} value={p.last ? monthLabel(p.last) : "–"} sub={<><PillView pill={pill(p.followUp.kurz, p.followUp.tone)} />{p.followUp.detail && <span className="tk-kpi-zusatz">{p.followUp.detail}</span>}</>} />
          {amounts && (
            <Kpi label={t("Offene Forderungen", "Open receivables")} value={p.open.count ? sumsLabel(p.open.sums, money) : m(0)}
              sub={t(`${p.open.count === 1 ? t("1 Rechnung", "1 invoice") : t(`${p.open.count} Rechnungen`, `${p.open.count} invoices`)} · brutto${overdueCount ? t(` · ${overdueCount} überfällig`, ` · ${overdueCount} overdue`) : ""}`, `${p.open.count === 1 ? t("1 Rechnung", "1 invoice") : t(`${p.open.count} Rechnungen`, `${p.open.count} invoices`)} · gross${overdueCount ? t(` · ${overdueCount} überfällig`, ` · ${overdueCount} overdue`) : ""}`)} />
          )}
          <Kpi label={t("Verträge und Rechnungen", "Contracts and invoices")} value={`${p.contracts.length} · ${p.invoices.length}`} sub={t("Verträge · Rechnungen an die Marke", "Contracts · invoices to the brand")} />
        </div>
      </section>
      <section>
        <div className="group-label">{t("Laufendes Jahr · ", "Current year · ")}{year}</div>
        <div className="overview-kpis talent-kpis more">
          {amounts && <Kpi label={t("Honorarvolumen", "Fee volume")} chip={year} value={m(p.year.gross)} sub={t("Netto · verbucht und geplant", "Net · booked and planned")} />}
          {financials && <Kpi label={t("Agentur-Einnahmen", "Agency income")} chip={year} value={m(p.year.agency)} sub={t(`Netto · davon verbucht ${m(p.year.bookedAgency)}`, `Net · of which booked ${m(p.year.bookedAgency)}`)} />}
          <Kpi label={t("Kooperationen", "Deals")} chip={year} value={p.year.deals} sub={t("mit Umsatz in diesem Jahr", "with revenue this year")} />
        </div>
      </section>
      {!amounts ? <p className="source-note">{HIDDEN_AMOUNTS()}</p> : !financials && !ich ? <p className="source-note">{HIDDEN_FINANCIALS()}</p> : null}

      <div className="grid tk-oben">
        {ich ? null : financials ? (
          <section className="panel">
            <h3>{t("Creator dieser Marke (Agentur-Einnahmen netto)", "Creators of this brand (agency income net)")}</h3>
            <RankRows
              empty={t("Noch keine Umsätze mit Creator.", "No revenue with creators yet.")}
              rows={p.talents.slice(0, 8).map((r) => ({ key: r.id, label: <Link to={`/agentur/creator/${r.id}`}>{talentName(r.id)}</Link>, value: r.agency, display: shortMoney(r.agency, base.currency) }))}
            />
          </section>
        ) : (
          <section className="panel stack">
            <h3>{t("Creator dieser Marke", "Creators of this brand")}</h3>
            {p.talents.length ? p.talents.slice(0, 8).map((r) => <Link key={r.id} to={`/agentur/creator/${r.id}`}>{talentName(r.id)}</Link>) : <p>{t("Noch keine Kooperationen mit Creator.", "No deals with creators yet.")}</p>}
          </section>
        )}
        <section className="panel stack">
          <h3>{t("Kontakte · ", "Contacts · ")}{own.length}</h3>
          <ContactsEditor brandId={b.id} contacts={own} />
        </section>
      </div>

      <h2>{t("Kooperationen", "Deals")}</h2>
      <DealTable lines={p.deals} partner="talent" caption={t(`Kooperationen mit ${b.name}`, `Deals with ${b.name}`)} empty={b.kind === "agency" ? t("Keine eigenen Kooperationen; die Kooperationen der betreuten Marken stehen unten.", "No own deals; the deals of the managed brands are listed below.") : t("Noch keine Kooperationen mit dieser Marke.", "No deals with this brand yet.")} />

      {p.via && (
        <>
          <h2>{t("Über betreute Marken · ", "Via managed brands · ")}{p.represented.length}</h2>
          <p>{t("Diese Zahlen zählen bei den Marken selbst; hier stehen sie zusammengefasst für die Agentur und nie doppelt in Summen.", "These figures count for the brands themselves; here they are summarised for the agency and never counted twice in totals.")}</p>
          <div className="overview-kpis period">
            <Kpi label={t("Kooperationen", "Deals")} value={p.via.deals.length} sub={t(`${p.via.deals.filter((l) => l.active).length} aktiv`, `${p.via.deals.filter((l) => l.active).length} active`)} />
            {amounts && <Kpi label={t("Honorarvolumen", "Fee volume")} chip={year} value={m(p.via.year.gross)} sub={t("Netto · betreute Marken", "Net · managed brands")} />}
            {amounts && (
              <Kpi label={t("Offene Forderungen", "Open receivables")} value={p.via.open.count ? sumsLabel(p.via.open.sums, money) : m(0)}
                sub={t(`${p.via.open.count === 1 ? t("1 Rechnung", "1 invoice") : t(`${p.via.open.count} Rechnungen`, `${p.via.open.count} invoices`)} · brutto${p.via.overdue ? t(` · ${p.via.overdue.count} überfällig`, ` · ${p.via.overdue.count} overdue`) : ""}`, `${p.via.open.count === 1 ? t("1 Rechnung", "1 invoice") : t(`${p.via.open.count} Rechnungen`, `${p.via.open.count} invoices`)} · gross${p.via.overdue ? t(` · ${p.via.overdue.count} überfällig`, ` · ${p.via.overdue.count} overdue`) : ""}`)} />
            )}
          </div>
          <DealTable lines={p.via.deals} partner="talent" caption={t(`Kooperationen der Marken über ${b.name}`, `Deals of the brands via ${b.name}`)} empty={t("Noch keine Kooperationen der betreuten Marken.", "No deals of the managed brands yet.")} />
        </>
      )}

      <div className="grid tk-oben">
        <section className="panel stack">
          <div className="row between">
            <h3>{t("Verträge · ", "Contracts · ")}{p.contracts.length}</h3>
            <Link className="btn" to="/agentur/vertraege">{t("Alle Verträge", "All contracts")}</Link>
          </div>
          <ContractList contracts={p.contracts} partnerName={(c) => talentName(c.talent_id)} empty={t("Keine Verträge mit dieser Marke.", "No contracts with this brand.")} />
        </section>
        <div className="stack">
          <BrandNotePanel ws={ws} brandId={b.id} />
          <BrandVorlagenPanel ws={ws} brandId={b.id} />
          {b.kind === "agency" && (
            <section className="panel stack">
              <h3>{t("Betreute Marken · ", "Managed brands · ")}{p.represented.length}</h3>
              {p.represented.length
                ? p.represented.map((r) => (
                    <Link key={r.id} className="task" to={`/agentur/marken/${r.id}`}>
                      <div><strong>{r.name}</strong><p>{r.invoice_via_agency ? t("Rechnung an diese Agentur", "Invoice to this agency") : t("Rechnung an die Marke", "Invoice to the brand")}</p></div>
                      <span className="pill info"><span className="pill-zeichen">◇</span> {t("Betreut", "Managed")}</span>
                    </Link>
                  ))
                : <p className="muted">{t("Noch keine Marke ist dieser Agentur zugeordnet. Die Zuordnung erfolgt an der Marke („Kontakt läuft über“).", "No brand is assigned to this agency yet. The assignment is made on the brand (“Contact goes through”).")}</p>}
            </section>
          )}
        </div>
      </div>

      <h2 id="rechnungen">{t("Rechnungen · ", "Invoices · ")}{p.invoices.length}</h2>
      <InvoiceTable
        views={p.invoices}
        caption={t(`Rechnungen an ${b.name}`, `Invoices to ${b.name}`)}
        // Studio (Creator-Konto): nur der Deal, ohne den eigenen Namen
        partnerLabel={ich ? "Deal" : t("Creator / Kooperation", "Creator / deal")}
        partnerName={(v) => [ich ? null : talentName(v.row.talent_id), v.row.deal_id ? base.dealById.get(v.row.deal_id)?.title : null].filter((x) => x && x !== "–").join(" · ") || "–"}
        empty={t("Keine Rechnungen an diese Marke.", "No invoices to this brand.")}
      />

      {p.bookings.length > 0 && (
        <>
          <h2>{t("Buchungen und Sonderposten", "Bookings and special items")}</h2>
          <BookingTable bookings={p.bookings} caption={t(`Buchungen zu ${b.name}`, `Bookings for ${b.name}`)} partnerName={(x) => (x.talent_id ? talentName(x.talent_id) : ta("Agentur", "Agency", "Eigenes Konto", "Own account"))}
            dealTitle={(x) => (x.deal_id ? base.dealById.get(x.deal_id)?.title ?? null : null)} />
        </>
      )}
      <p className="source-note">
        {t("Umsätze sind netto und gerechnet wie auf der Übersicht (Kooperationsmonate und Buchungen bis zum laufenden Monat). Offene Forderungen sind brutto.", "Revenue is net and calculated as in the overview (deal months and bookings up to the current month). Open receivables are gross.")}{ws.isChef || ws.membership.sees_all_talents ? "" : t(" Du siehst nur Kooperationen und Rechnungen deiner zugewiesenen Creator.", " You only see deals and invoices of your assigned creators.")}
      </p>
      <DemoNote />
      {edit && <BrandDialog brand={b} base={base} contacts={contacts} onClose={() => setEdit(false)} />}
    </main>
  );
}

function shortMoney(cents: number, currency: string): string {
  return moneyWhole(cents, currency);
}

function BrandNotePanel({ ws, brandId }: { ws: StaffWorkspace; brandId: string }) {
  const note = useBrandNote(ws.org.org_id, brandId);
  const [text, setText] = useState<string | null>(null);
  const { busy, error, run } = useAction();
  const saved = note.data?.note ?? "";
  const value = text ?? saved;
  const dirty = text !== null && text !== saved;
  return (
    <section className="panel stack">
      <h3>{t("Interne Notiz", "Internal note")}</h3>
      <label>
        {t("Notiz", "Note")}<textarea value={value} onChange={(e) => setText(e.target.value)} disabled={note.isPending} aria-describedby="marke-notiz-hinweis" placeholder={t("Briefing-Vorlieben, Zuständigkeiten, Bestellnummern …", "Briefing preferences, responsibilities, order numbers …")} />
      </label>
      <small id="marke-notiz-hinweis">{ta("Nur für das Team sichtbar.", "Only visible to the team.", "Nur für dich sichtbar, nie für die Marke.", "Only visible to you, never to the brand.")}{note.data ? t(` Zuletzt geändert am ${date(note.data.updated_at, ws.org.timezone)}.`, ` Last changed on ${date(note.data.updated_at, ws.org.timezone)}.`) : ""}</small>
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row">
        <button className="primary" disabled={!dirty || !!busy} aria-busy={!!busy}
          onClick={async () => { if (await run("notiz", () => saveBrandNote(api, ws.org.org_id, brandId, value), t("Notiz gespeichert.", "Note saved."))) setText(null); }}>
          {busy ? t("Wird gespeichert …", "Saving …") : t("Notiz speichern", "Save note")}
        </button>
      </div>
    </section>
  );
}

/** PDF-Vorlage je Belegart für diese Marke (0456, wie bei Zoho je Kunde); leer = Standard der Agentur */
function BrandVorlagenPanel({ ws, brandId }: { ws: StaffWorkspace; brandId: string }) {
  const orgId = ws.org.org_id;
  const qc = useQueryClient();
  const vorlagen = useQuery({ queryKey: ["document_templates", orgId], queryFn: () => listeVorlagen(orgId) });
  const zuordnung = useQuery({ queryKey: ["brand_document_templates", brandId], queryFn: () => api.select<{ document_templates: Record<string, string> | null }>("brands", { columns: "document_templates", filters: [["id", "eq", brandId]], limit: 1 }).then((r) => r[0]?.document_templates ?? {}) });
  const { busy, run } = useAction();
  // Prüf-Chat P4: Vorlage je Marke wählt nur, wer Vorlagen bearbeiten darf (wie die PDF-Vorlagen selbst)
  if (!vorlagen.data?.length || !ws.can("edit_templates")) return null;
  const arten = BELEG_ARTEN.filter((a) => vorlagen.data.some((v) => v.kind === a.art));
  const waehlen = (art: string, id: string) => void run(`vorlage-${art}`, async () => {
    const neu = { ...(zuordnung.data ?? {}) };
    if (id) neu[art] = id; else delete neu[art];
    await api.update("brands", [["id", "eq", brandId]], { document_templates: neu }, { returning: false });
    await qc.invalidateQueries({ queryKey: ["brand_document_templates", brandId] });
    return true;
  }, t("PDF-Vorlage gespeichert.", "PDF template saved."));
  return (
    <section className="panel stack">
      <h3>{t("PDF-Vorlagen für diese Marke", "PDF templates for this brand")}</h3>
      {arten.map(({ art, name }) => {
        const std = vorlagen.data.find((v) => v.kind === art && v.is_default);
        return (
          <label key={art}>{name()}
            <select aria-label={t(`PDF-Vorlage ${name()}`, `PDF template ${name()}`)} value={zuordnung.data?.[art] ?? ""} disabled={!!busy || zuordnung.isPending} onChange={(e) => waehlen(art, e.target.value)}>
              <option value="">{std ? ta(`Standard der Agentur (${std.name})`, `Agency default (${std.name})`, `Standard (${std.name})`, `Default (${std.name})`) : t("Standard-Layout von CreatorOffice", "CreatorOffice standard layout")}</option>
              {vorlagen.data.filter((v) => v.kind === art).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </label>
        );
      })}
      <small className="muted">{t("Gilt für PDFs an diese Marke, die ab jetzt entstehen; schon abgelegte bleiben. Vorlagen bearbeitest du unter ", "Applies to PDFs to this brand created from now on; archived ones stay. You edit templates under ")}<Link to="/agentur/einstellungen/pdf-vorlagen">{t("PDF-Vorlagen", "PDF templates")}</Link>.</small>
    </section>
  );
}
